import { Box, useToast } from '@chakra-ui/react';
import React from 'react';
import ReactDiffViewer from 'react-diff-viewer';

import { artifactsDocumentToTapeBuilderGraph } from '@finley-technologies/tape-builder';

import { TapeBuilderDiffDocument } from './TapeBuilderDiffDocument';
import { useGetBorrowingBasePipelineDocumentInfoQuery } from '../requests/tapeBuilder.gql.generated';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';

type Props = { diff: TapeBuilderDiffDocument };
export const TapeBuilderDiffViewer = ({ diff }: Props) => {
  const toast = useToast();
  const oldData = useGetBorrowingBasePipelineDocumentInfoQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      artifactDocumentId: diff.oldValue.id, // value for 'artifactDocumentId'
      dbView: true,
    },
  });

  const newData = useGetBorrowingBasePipelineDocumentInfoQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      artifactDocumentId: diff.newValue.id, // value for 'artifactDocumentId'
      dbView: true,
    },
  });

  if (oldData.loading) {
    return <Box>Loading left...</Box>;
  }
  if (oldData.error) {
    return <Box>Error left</Box>;
  }
  if (oldData.data?.borrowingBasePipelineArtifactDocumentInfo == null) {
    return <Box>Left document not found</Box>;
  }

  if (newData.loading) {
    return <Box>Loading right...</Box>;
  }
  if (newData.error) {
    return <Box>Error right</Box>;
  }
  if (newData.data?.borrowingBasePipelineArtifactDocumentInfo == null) {
    return <Box>Right Document not found</Box>;
  }

  const convertGraph = (artifactsDocumentString: string) => {
    const { graph } = artifactsDocumentToTapeBuilderGraph({
      artifactsDocument: artifactsDocumentString,
    });

    // graphs have ids that are not stable, so we need to convert them to slugs
    // to make it easier to compare
    const idToSlug = new Map<string, string>();
    const nodes = graph.nodes
      .map((node) => {
        const {
          // @ts-ignore
          artifactSlug,
          // @ts-ignore
          id,
          // @ts-ignore
          artifactDocumentId: _artifactDocumentId,
          ...rest
        } = node.configuration;
        idToSlug.set(id, artifactSlug);
        return {
          ...rest,
          artifactSlug,
        };
      })
      .sort((a, b) => a.artifactSlug.localeCompare(b.artifactSlug));

    const edges = graph.edges

      .map((edge) => {
        return {
          source: idToSlug.get(edge.source) || '',
          target: idToSlug.get(edge.target) || '',
        };
      })
      .sort((a, b) => (a.source + a.target).localeCompare(b.source + b.target));

    return JSON.stringify({ nodes, edges }, null, 2);
  };
  const oldValue = convertGraph(
    oldData.data.borrowingBasePipelineArtifactDocumentInfo
      .artifactsDocumentString,
  );
  const newValue = convertGraph(
    newData.data.borrowingBasePipelineArtifactDocumentInfo
      .artifactsDocumentString,
  );

  return (
    <Box mt="24px" maxHeight="70vh" overflowY="scroll">
      <ReactDiffViewer
        oldValue={oldValue}
        newValue={newValue}
        leftTitle={`${diff.oldValue.id} - ${diff.oldValue.version}`}
        rightTitle={`${diff.newValue.id} - ${diff.newValue.version}`}
        useDarkTheme
        splitView
      />
    </Box>
  );
};
